import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import type { ApiResponse, ApiError } from '@/types/api';
import { appConfig, storageKeys } from '@/config/app';

export const normaliseApiError = (error: Pick<AxiosError, 'message' | 'response'>): ApiError => {
  const status = error.response?.status ?? 500;
  const responseData = error.response?.data as any;
  const durableDelivery = responseData?.data ?? responseData;

  return {
    message: responseData?.error ?? responseData?.message ?? error.message ?? 'An unexpected error occurred',
    statusCode: status,
    errors: responseData?.errors ?? {},
    response: error.response,
    deliveryRecordId: responseData?.deliveryRecordId ?? durableDelivery?.deliveryRecordId,
    messageId: responseData?.messageId ?? durableDelivery?.messageId,
    retryEndpoint: responseData?.retryEndpoint ?? durableDelivery?.retryEndpoint,
  };
};

// API Client Class
class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: appConfig.apiBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public get defaults() {
    return this.client.defaults;
  }

  private setupInterceptors() {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request timestamp for debugging
        if (appConfig.environment === 'development') {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle token refresh
    this.client.interceptors.response.use(
      (response) => {
        // Log successful responses in development
        if (appConfig.environment === 'development') {
          console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data,
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 errors with token refresh (excluding auth endpoints like login/register/refresh)
        const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                               originalRequest?.url?.includes('/auth/register') || 
                               originalRequest?.url?.includes('/auth/refresh') ||
                               originalRequest?.url?.includes('/auth/forgot-password');

        if (error.response?.status === 401 && !isAuthEndpoint) {
          if (!originalRequest._retry) {
            if (this.isRefreshing) {
              // If already refreshing, queue the request
              return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
              }).then(token => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                return this.client(originalRequest);
              });
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            try {
              const refreshToken = this.getRefreshToken();
              if (!refreshToken) {
                throw new Error('No refresh token available');
              }

              const response = await this.refreshAccessToken(refreshToken);
              const { accessToken } = response.data;

              this.setAccessToken(accessToken);
              this.processQueue(null, accessToken);

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }
              return this.client(originalRequest);

            } catch (refreshError) {
              this.processQueue(refreshError, null);
              this.clearTokens();
              // Redirect to login page if session expired
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            // Already retried and still 401 - clear tokens and redirect to login
            this.clearTokens();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }

        // Log errors in development
        if (appConfig.environment === 'development') {
          console.error(`[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  private handleError(error: AxiosError): ApiError {
    return normaliseApiError(error);
  }

  private async refreshAccessToken(refreshToken: string): Promise<AxiosResponse<{ accessToken: string }>> {
    return this.client.post('/auth/refresh', {
      refreshToken,
    });
  }

  // Token management
  private getAccessToken(): string | null {
    return localStorage.getItem(storageKeys.accessToken);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(storageKeys.refreshToken);
  }

  private setAccessToken(token: string): void {
    localStorage.setItem(storageKeys.accessToken, token);
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem(storageKeys.refreshToken, token);
  }

  private clearTokens(): void {
    localStorage.removeItem(storageKeys.accessToken);
    localStorage.removeItem(storageKeys.refreshToken);
    localStorage.removeItem(storageKeys.user);
  }

  // Public methods
  public setTokens(accessToken: string, refreshToken: string): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  public logout(): void {
    this.clearTokens();
  }

  public isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Reject an SPA fallback served in place of an API response.
   *
   * A request to a path the API does not expose is answered by the static file handler
   * with index.html and HTTP 200. Without this check wrapResponse would mark that HTML
   * as a successful payload, callers would read undefined off a string, and the page
   * would render empty with no error raised - the failure mode behind BugHerd #138.
   *
   * Only the JSON helpers below call this. Blob and file downloads go through
   * axiosClient or download() directly and never reach it, and a Blob would not satisfy
   * the string check in any case.
   */
  private assertJsonResponse(response: AxiosResponse<any>): void {
    const contentType = String(response.headers?.['content-type'] ?? '');
    if (typeof response.data === 'string' && contentType.includes('text/html')) {
      // Thrown as a real Error so callers that test `instanceof Error` keep the
      // message, while still carrying statusCode like the interceptor's ApiError.
      throw Object.assign(
        new Error(`The API returned an HTML page instead of data for ${response.config?.url ?? 'this request'}. The route is probably not exposed by the API.`),
        { statusCode: response.status } as Pick<ApiError, 'statusCode'>
      );
    }
  }

  // Helper to check if response is already wrapped
  private isWrappedResponse(data: any): boolean {
    return data && typeof data === 'object' && 'success' in data && 'data' in data;
  }

  // Helper to wrap raw responses
  private wrapResponse<T>(data: T): ApiResponse<T> {
    if (this.isWrappedResponse(data)) {
      return data as ApiResponse<T>;
    }
    return {
      success: true,
      data: data,
      message: 'Success'
    };
  }

  // HTTP methods
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<T>(url, config);
    this.assertJsonResponse(response);
    return this.wrapResponse(response.data);
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<T>(url, data, config);
    this.assertJsonResponse(response);
    return this.wrapResponse(response.data);
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<T>(url, data, config);
    this.assertJsonResponse(response);
    return this.wrapResponse(response.data);
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<T>(url, data, config);
    this.assertJsonResponse(response);
    return this.wrapResponse(response.data);
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<T>(url, config);
    this.assertJsonResponse(response);
    return this.wrapResponse(response.data);
  }

  // File upload
  public async upload<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
      ...config,
    });

    return response.data;
  }

  // Download file
  public async download(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename ?? 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Health check
  public async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.get('/health');
  }

  // Expose the axios client for direct access when needed
  public get axiosClient(): AxiosInstance {
    return this.client;
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();

// Export the class for testing or custom instances
export { ApiClient };

// Helper function for handling API calls with loading states
export const withApiLoading = async <T>(
  apiCall: () => Promise<T>,
  onLoadingChange?: (loading: boolean) => void
): Promise<T> => {
  try {
    onLoadingChange?.(true);
    const result = await apiCall();
    return result;
  } finally {
    onLoadingChange?.(false);
  }
};

// Helper function for handling API errors
export const handleApiError = (error: ApiError, defaultMessage = 'An error occurred'): string => {
  if (error.errors && Object.keys(error.errors).length > 0) {
    // Return first validation error
    const firstField = Object.keys(error.errors)[0];
    const firstError = error.errors[firstField]?.[0];
    return firstError ?? error.message ?? defaultMessage;
  }

  return error.message ?? defaultMessage;
};
